/**
 * Settings page - Application settings
 */

import {useState} from 'react';
import {useUIStore, useAuthStore} from '@voxapilot/shared';
import {api} from '../config/firebase';
import {Button} from '../components/ui/Button';
import {cn} from '../utils/cn';

export function SettingsPage() {
  const {theme, setTheme, sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed} = useUIStore();
  const {signOut} = useAuthStore();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut(api.session);
      // Navigation will happen via ProtectedRoute
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage your application preferences
        </p>
      </div>

      {/* Appearance Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Appearance
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Theme
            </label>
            <div className="flex space-x-3">
              {(['light', 'dark', 'system'] as const).map((option) => (
                <button
                  key={option}
                  onClick={() => setTheme(option)}
                  className={cn(
                    'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                    theme === option
                      ? 'bg-orange-600 dark:bg-orange-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  )}
                >
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Interface Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Interface
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Show Sidebar
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Toggle sidebar visibility
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                sidebarOpen ? 'bg-orange-600 dark:bg-orange-500' : 'bg-gray-200 dark:bg-gray-700'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  sidebarOpen ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {sidebarOpen && (
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Collapse Sidebar
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Show sidebar in collapsed mode
                </p>
              </div>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={cn(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  sidebarCollapsed ? 'bg-orange-600 dark:bg-orange-500' : 'bg-gray-200 dark:bg-gray-700'
                )}
              >
                <span
                  className={cn(
                    'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                    sidebarCollapsed ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Account Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Account
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Sign Out
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Sign out of your account
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing out...' : 'Sign Out'}
            </Button>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          About
        </h2>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>
            <strong>VoxaPilot</strong> - AI-Powered Meeting Assistant
          </p>
          <p>Version 0.1.0</p>
          <p className="pt-4">
            VoxaPilot helps you record, transcribe, and summarize your meetings
            automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
